import { useState, useMemo, useEffect } from 'react'
import { format } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table'
import { useAnalyticsRequests } from '../api/analytics'
import Layout from '../components/Layout'
import DateRangePicker from '../components/DateRangePicker'
import RequestDetailModal from '../components/RequestDetailModal'
import type { RequestRecord, Verdict } from '../types/analytics'

const columnHelper = createColumnHelper<RequestRecord>()

const verdictColors: Record<Verdict, string> = {
  APPROVED: 'bg-verdict-approved/20 text-verdict-approved px-2 py-1 rounded-sm text-xs font-medium',
  REJECTED: 'bg-verdict-rejected/20 text-verdict-rejected px-2 py-1 rounded-sm text-xs font-medium',
  INCONCLUSIVE: 'bg-verdict-inconclusive/20 text-verdict-inconclusive px-2 py-1 rounded-sm text-xs font-medium',
  ERROR: 'bg-noir-tertiary text-text-secondary px-2 py-1 rounded-sm text-xs font-medium',
}

export default function Requests() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize date range from URL params or default to last 7 days
  // Use UTC dates since backend stores timestamps in UTC
  const getInitialDateRange = () => {
    const urlStart = searchParams.get('start_date')
    const urlEnd = searchParams.get('end_date')

    // Use UTC to match backend timestamp storage
    const now = new Date()
    const utcToday = now.toISOString().split('T')[0]
    const sixDaysAgoMs = now.getTime() - 6 * 24 * 60 * 60 * 1000
    const utcSixDaysAgo = new Date(sixDaysAgoMs).toISOString().split('T')[0]

    return {
      start: urlStart || utcSixDaysAgo,
      end: urlEnd || utcToday,
    }
  }

  const [dateRange, setDateRange] = useState(getInitialDateRange)
  const [verdictFilter, setVerdictFilter] = useState<Verdict | ''>('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [selectedRequest, setSelectedRequest] = useState<RequestRecord | null>(null)

  // Sync URL params when date range changes
  useEffect(() => {
    setSearchParams(
      { start_date: dateRange.start, end_date: dateRange.end },
      { replace: true }
    )
  }, [dateRange.start, dateRange.end, setSearchParams])

  const { data, isLoading, error } = useAnalyticsRequests({
    start_date: dateRange.start,
    end_date: dateRange.end,
    verdict: verdictFilter || undefined,
    limit: 100,
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor('timestamp', {
        header: 'Timestamp',
        cell: (info) => format(new Date(info.getValue()), 'MMM d, HH:mm'),
      }),
      columnHelper.accessor('verdict', {
        header: 'Verdict',
        cell: (info) => (
          <span className={verdictColors[info.getValue()]}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('filename', {
        header: 'File',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('content_lines', {
        header: 'Lines',
        cell: (info) => info.getValue().toLocaleString(),
      }),
      columnHelper.accessor('total_voting_rounds', {
        header: 'Rounds',
      }),
      columnHelper.accessor('total_duration_ms', {
        header: 'Latency',
        cell: (info) => `${(info.getValue() / 1000).toFixed(1)}s`,
      }),
    ],
    []
  )

  const table = useReactTable({
    data: data?.requests || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-text-primary font-mono">Request Analysis</h1>

          <div className="flex flex-wrap items-center gap-4">
            <DateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={(start, end) => setDateRange({ start, end })}
            />

            <label htmlFor="verdict-filter" className="sr-only">Filter by verdict</label>
            <select
              id="verdict-filter"
              value={verdictFilter}
              onChange={(e) => setVerdictFilter(e.target.value as Verdict | '')}
              className="input w-auto"
              aria-label="Filter requests by verdict"
            >
              <option value="">All Verdicts</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="INCONCLUSIVE">Inconclusive</option>
              <option value="ERROR">Error</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div
                className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
                aria-label="Loading requests"
              >
                <span className="sr-only">Loading requests...</span>
              </div>
              <p className="mt-4 text-sm text-text-secondary">Loading request data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-6">
            <div className="flex">
              <div className="shrink-0">
                <svg
                  className="h-5 w-5 text-verdict-rejected"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-verdict-rejected">Error loading requests</h3>
                <p className="mt-2 text-sm text-verdict-rejected">
                  {error instanceof Error ? error.message : 'Failed to load requests'}
                </p>
              </div>
            </div>
          </div>
        ) : data?.requests.length === 0 ? (
          <div className="card py-16">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">No requests found</h3>
              <p className="mt-1 text-sm text-text-secondary">
                Try adjusting your filters or selecting a different date range.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-noir-secondary border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-noir-tertiary">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                          >
                            <button
                              onClick={header.column.getToggleSortingHandler()}
                              className="flex items-center gap-1 w-full hover:text-text-primary focus:outline-hidden focus:text-accent-cyan transition-colors"
                              aria-label={`Sort by ${header.column.columnDef.header as string}`}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <span aria-hidden="true">
                                {{
                                  asc: ' ↑',
                                  desc: ' ↓',
                                }[header.column.getIsSorted() as string] ?? null}
                              </span>
                            </button>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-border">
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedRequest(row.original)}
                        className="hover:bg-noir-tertiary cursor-pointer transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-cyan focus-visible:ring-inset"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedRequest(row.original)
                          }
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-4 text-sm text-text-primary whitespace-nowrap"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-sm text-text-secondary">
              Showing {data?.requests.length || 0} of {data?.total_count || 0} requests
              {data?.has_more && ' (more available)'}
            </div>
          </>
        )}

        {/* Request Detail Modal */}
        {selectedRequest && (
          <RequestDetailModal
            request={selectedRequest}
            onClose={() => setSelectedRequest(null)}
          />
        )}
      </div>
    </Layout>
  )
}
